import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogTrigger, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import api from '../api/client';
import { Plus, ArrowRight, Loader2, TrendingUp, RefreshCw, Settings, Zap, BarChart3 } from 'lucide-react';

const toTitleCase = (s) => s.replace(/_/g, ' ').replace(/^ai /, '').replace(/\b\w/g, c => c.toUpperCase());

const CREDIT_PACKS = [
  { credits: 200,   price: 10,   label: '200',    save: null },
  { credits: 1100,  price: 50,   label: '1,100',  save: '10%' },
  { credits: 2300,  price: 100,  label: '2,300',  save: '15%' },
  { credits: 4800,  price: 200,  label: '4,800',  save: '20%' },
  { credits: 12500, price: 500,  label: '12,500', save: '25%', recommended: true },
  { credits: 26000, price: 1000, label: '26,000', save: '30%' },
];

function CoinIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="11" fill="url(#cg)" stroke="#C8960B" strokeWidth="0.6" />
      <circle cx="12" cy="12" r="8.5" fill="none" stroke="#DAA520" strokeWidth="0.4" opacity="0.5" />
      <path d="M12 6L13.5 10.5L18 12L13.5 13.5L12 18L10.5 13.5L6 12L10.5 10.5L12 6Z" fill="#7a5c00" fillOpacity="0.7" />
      <path d="M12 7.5L13 10.8L16.5 12L13 13.2L12 16.5L11 13.2L7.5 12L11 10.8L12 7.5Z" fill="#FFD700" fillOpacity="0.35" />
      <defs>
        <radialGradient id="cg" cx="0.35" cy="0.35" r="0.65">
          <stop offset="0%" stopColor="#FFE580" />
          <stop offset="40%" stopColor="#FFD700" />
          <stop offset="75%" stopColor="#DAA520" />
          <stop offset="100%" stopColor="#C8960B" />
        </radialGradient>
      </defs>
    </svg>
  );
}

function CoinSmall() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="11" fill="url(#csm)" stroke="#C8960B" strokeWidth="0.8" />
      <path d="M12 6L13.5 10.5L18 12L13.5 13.5L12 18L10.5 13.5L6 12L10.5 10.5L12 6Z" fill="#7a5c00" fillOpacity="0.7" />
      <defs>
        <radialGradient id="csm" cx="0.35" cy="0.35" r="0.65">
          <stop offset="0%" stopColor="#FFE580" />
          <stop offset="50%" stopColor="#FFD700" />
          <stop offset="100%" stopColor="#C8960B" />
        </radialGradient>
      </defs>
    </svg>
  );
}

export default function CreditCounter() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [credits, setCredits] = useState(null);
  const [buying, setBuying] = useState(null);
  const [chosenPack, setChosenPack] = useState(CREDIT_PACKS.find(p => p.recommended) || null);
  const [showSettings, setShowSettings] = useState(false);
  const [replenish, setReplenish] = useState({ enabled: false, threshold: 500, pack_credits: 2300 });
  const [savingReplenish, setSavingReplenish] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'owner' || user?.role === 'manager';

  const fetchCredits = useCallback(async () => {
    try {
      const resp = await api.get('/api/billing/credits');
      setCredits(resp.data);
      if (resp.data.auto_replenish) {
        setReplenish(resp.data.auto_replenish);
      }
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (isAdmin) fetchCredits();
    const iv = setInterval(() => { if (isAdmin) fetchCredits(); }, 60000);
    return () => clearInterval(iv);
  }, [isAdmin, fetchCredits]);

  if (!isAdmin || !credits) return null;

  const remaining = credits.remaining ?? 0;
  const limit = credits.credits_limit ?? 1;
  const used = credits.credits_used ?? 0;
  const pct = credits.usage_percent ?? 0;
  const overageRate = credits.overage_rate ?? 0.05;

  const handleBuy = async () => {
    if (!chosenPack) return;
    setBuying(chosenPack.credits);
    try {
      const r = await api.post('/api/billing/credits/topup', { credits: chosenPack.credits, origin_url: window.location.origin });
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
    } catch { navigate('/billing'); }
    finally { setBuying(null); }
  };

  const handleReplenishToggle = async (enabled) => {
    setSavingReplenish(true);
    const newState = { ...replenish, enabled };
    setReplenish(newState);
    try {
      await api.post('/api/billing/auto-replenish', {
        enabled,
        threshold: newState.threshold || 500,
        pack_credits: newState.pack_credits || 2400,
      });
    } catch { /* silent */ }
    finally { setSavingReplenish(false); }
  };

  const handleReplenishSave = async () => {
    setSavingReplenish(true);
    try {
      await api.post('/api/billing/auto-replenish', {
        enabled: replenish.enabled,
        threshold: replenish.threshold,
        pack_credits: replenish.pack_credits,
      });
      setShowSettings(false);
    } catch { /* silent */ }
    finally { setSavingReplenish(false); }
  };

  const selectedPack = CREDIT_PACKS.find(p => p.credits === replenish.pack_credits) || CREDIT_PACKS[2];
  const barColor = pct > 90 ? '#E2445C' : pct > 70 ? '#FDAB3D' : 'linear-gradient(90deg, #DAA520, #FFD700)';

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          className="flex items-center gap-1.5 h-9 px-2 sm:px-3 rounded-full border border-border bg-muted text-foreground hover:bg-muted/80 transition-colors"
          data-testid="credit-counter-btn"
          title={`${remaining.toLocaleString()} AI credits remaining`}
        >
          <span className="w-5 h-5 flex items-center justify-center flex-shrink-0"><CoinIcon size={20} /></span>
          <span className="text-sm font-medium tabular-nums hidden sm:inline" data-testid="credit-count-display">{remaining.toLocaleString()}</span>
        </button>
      </DialogTrigger>

      <DialogContent className="max-w-3xl w-[calc(100vw-2rem)] sm:w-full p-0 gap-0 bg-background border-border max-h-[90vh] overflow-y-auto" data-testid="credit-modal">
        {/* Header */}
        <div className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4 border-b border-border">
          <div className="flex items-center justify-between mb-3 sm:mb-4 pr-8">
            <DialogTitle className="flex items-center gap-2">
              <CoinIcon size={24} />
              <span className="text-base sm:text-lg font-semibold text-foreground">AI Credits</span>
            </DialogTitle>
            <span className="text-xs sm:text-sm px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full font-semibold uppercase tracking-wider bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
              {credits.plan || 'Free'}
            </span>
          </div>

          {/* Balance bar */}
          <div className="flex items-end justify-between mb-2.5">
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold tabular-nums text-foreground" data-testid="modal-credit-balance">{remaining.toLocaleString()}</span>
              <span className="text-sm text-muted-foreground">/ {limit.toLocaleString()} credits</span>
            </div>
            {credits.overage_credits > 0 && (
              <span className="text-sm font-medium text-[#E2445C]">
                +{credits.overage_credits} overage (${credits.overage_cost})
              </span>
            )}
          </div>
          <div className="relative h-2 rounded-full overflow-hidden bg-muted">
            <div
              className="absolute inset-y-0 left-0 rounded-full transition-all duration-500"
              style={{ width: `${Math.min(pct, 100)}%`, background: barColor }}
            />
          </div>
          <div className="flex justify-between mt-2">
            <p className="text-sm text-muted-foreground">{used.toLocaleString()} used this cycle</p>
            {credits.plan_id !== 'free' && (
              <p className="text-sm text-muted-foreground">Overage rate: ${overageRate}/credit</p>
            )}
          </div>
        </div>

        {/* Credit Packs — full-width grid */}
        <div className="px-4 sm:px-6 pt-2.5 pb-2">
          <p className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
            <Plus className="w-4 h-4" /> Add Credits
          </p>
          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            {CREDIT_PACKS.map((pack) => {
              const isSelected = chosenPack?.credits === pack.credits;
              return (
                <button
                  key={pack.credits}
                  onClick={() => setChosenPack(isSelected ? null : pack)}
                  className={`relative group flex flex-col items-center gap-1 sm:gap-1.5 py-3 sm:py-5 px-2 sm:px-3 rounded-xl border-2 transition-all duration-200 ${
                    isSelected
                      ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20 ring-1 ring-amber-400/50'
                      : pack.recommended
                        ? 'border-[#0073EA] bg-[#0073EA]/5 hover:bg-[#0073EA]/10'
                        : 'border-border bg-muted/30 hover:border-amber-400 hover:bg-gradient-to-b hover:from-amber-50 hover:to-transparent dark:hover:from-amber-900/15 dark:hover:to-transparent'
                  }`}
                  data-testid={`buy-${pack.credits}-credits`}
                >
                  {pack.recommended && (
                    <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-[#0073EA] text-white shadow-sm whitespace-nowrap">
                      MOST POPULAR
                    </span>
                  )}
                  {pack.save && (
                    <span className="absolute -top-2.5 right-2 text-xs font-bold px-2 py-0.5 rounded-full bg-[#00C875] text-white shadow-sm">
                      {pack.save}
                    </span>
                  )}
                  <CoinIcon size={24} />
                  <span className="text-base sm:text-xl font-extrabold text-foreground tracking-tight mt-0.5 sm:mt-1">{pack.label}</span>
                  <span className="text-[11px] sm:text-sm text-muted-foreground text-center">credits for <span className="font-bold text-foreground">${pack.price}</span></span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Order summary strip — always visible */}
        <div className="px-4 sm:px-6 py-2.5 border-t border-border bg-muted/30 flex items-center min-h-[44px]" data-testid="order-summary">
          <div className="flex items-center justify-between w-full gap-4">
            <div className="flex items-center gap-3 min-w-0">
              {chosenPack ? (
                <>
                  <span className="flex items-center flex-shrink-0"><CoinIcon size={24} /></span>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold text-foreground">{chosenPack.label} Credits</span>
                    <span className="text-sm text-muted-foreground">for</span>
                    <span className="text-sm font-bold text-foreground">${chosenPack.price}.00</span>
                    {chosenPack.save && (
                      <span className="text-xs px-1.5 py-0.5 rounded-full bg-[#00C875]/10 text-[#00C875] font-semibold">{chosenPack.save} off</span>
                    )}
                    <span className="text-xs text-muted-foreground">({`$${parseFloat((chosenPack.price / chosenPack.credits).toFixed(3))}/credit`})</span>
                  </div>
                </>
              ) : (
                <span className="text-sm text-muted-foreground">Select a credit pack above</span>
              )}
            </div>
            <Button
              className="h-9 px-5 text-sm bg-[#0073EA] hover:bg-[#0060C0] text-white font-semibold flex-shrink-0"
              onClick={handleBuy}
              disabled={!!buying || !chosenPack}
              data-testid="proceed-to-pay-btn"
            >
              {buying ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
              {buying ? 'Redirecting...' : 'Proceed to Pay'}
            </Button>
          </div>
        </div>

        {/* Bottom row: Usage + Auto-replenish — always visible */}
        <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-border border-t border-border">
          {/* Usage breakdown */}
          <div className="px-3 sm:px-5 py-3">
            {credits.breakdown && Object.keys(credits.breakdown).length > 0 && (
              <div>
                <p className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                  <BarChart3 className="w-4 h-4" /> Credits Usage
                </p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  {Object.entries(credits.breakdown)
                    .sort((a, b) => b[1].credits - a[1].credits)
                    .map(([action, data]) => (
                      <div key={action} className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground truncate mr-1.5">{toTitleCase(action)}</span>
                        <span className="text-xs font-semibold tabular-nums text-foreground flex-shrink-0">{data.credits}</span>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>

          {/* Auto-replenish + Manage */}
          <div className="px-3 sm:px-5 py-3 flex flex-col gap-3">
            {showSettings ? (
              <div data-testid="replenish-settings">
                <p className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                  <RefreshCw className="w-4 h-4" /> Auto-Replenish Settings
                </p>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm text-muted-foreground block mb-1">When credits drop below</label>
                    <input
                      type="number"
                      value={replenish.threshold}
                      onChange={(e) => setReplenish(r => ({ ...r, threshold: parseInt(e.target.value) || 0 }))}
                      className="w-full h-9 px-3 text-sm rounded-md border border-border bg-background text-foreground"
                      data-testid="replenish-threshold-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground block mb-1">Auto-buy pack</label>
                    <select
                      value={replenish.pack_credits}
                      onChange={(e) => setReplenish(r => ({ ...r, pack_credits: parseInt(e.target.value) }))}
                      className="w-full h-9 px-3 text-sm rounded-md border border-border bg-background text-foreground"
                      data-testid="replenish-pack-select"
                    >
                      {CREDIT_PACKS.map(p => (
                        <option key={p.credits} value={p.credits}>{p.label} credits — ${p.price}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" className="flex-1 h-8 text-sm bg-[#0073EA] hover:bg-[#0060C0] text-white" onClick={handleReplenishSave} disabled={savingReplenish} data-testid="replenish-save-btn">
                      {savingReplenish ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                    </Button>
                    <Button size="sm" variant="outline" className="flex-1 h-8 text-sm" onClick={() => setShowSettings(false)}>Cancel</Button>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <RefreshCw className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-foreground font-medium">Auto-replenish</span>
                    {replenish.enabled && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-[#00C875]/10 text-[#00C875] font-medium">ON</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={replenish.enabled}
                      onCheckedChange={handleReplenishToggle}
                      className="scale-[0.8]"
                      data-testid="replenish-toggle"
                    />
                    <button onClick={() => setShowSettings(true)} className="p-1 rounded hover:bg-muted" data-testid="replenish-settings-btn">
                      <Settings className="w-3.5 h-3.5 text-muted-foreground" />
                    </button>
                  </div>
                </div>
                {replenish.enabled && (
                  <p className="text-xs text-muted-foreground mt-1 ml-6">
                    Below {replenish.threshold.toLocaleString()} credits → auto-buy {selectedPack.label} (${selectedPack.price})
                  </p>
                )}
              </div>
            )}

            {/* Manage billing */}
            <div className="pt-1">
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-between text-sm h-9"
                onClick={() => { setOpen(false); navigate('/billing'); }}
                data-testid="manage-billing-link"
              >
                <span className="flex items-center gap-1.5">
                  <TrendingUp className="w-3.5 h-3.5" /> Manage Plan & Usage
                </span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
